import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import JsBarcode from 'jsbarcode';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function BarcodePrintPage() {
  const [searchParams] = useSearchParams();
  const sku = searchParams.get('sku') || '';
  const name = searchParams.get('name') || '';
  const description = searchParams.get('description') || '';
  const svgRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');

  const renderBarcode = useCallback(() => {
    if (!sku || !svgRef.current) return;
    try {
      JsBarcode(svgRef.current, sku, {
        format: 'CODE128',
        width: 2,
        height: 60,
        displayValue: false,
        margin: 0,
        background: '#ffffff',
        lineColor: '#000000',
      });
      setReady(true);
    } catch (err) {
      setError('Failed to generate barcode: ' + err.message);
    }
  }, [sku]);

  useEffect(() => { renderBarcode(); }, [renderBarcode]);

  const handlePrintPDF = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams({ name, description });
      const url = `${API_URL}/api/barcode/label-pdf/${encodeURIComponent(sku)}?${params.toString()}`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to generate PDF');
      const blob = await res.blob();
      const pdfUrl = URL.createObjectURL(blob);
      window.open(pdfUrl, '_blank');
    } catch (err) {
      setError('Failed to generate barcode PDF: ' + err.message);
    }
  };

  // Auto-open PDF on load
  useEffect(() => {
    if (ready) {
      const timer = setTimeout(() => handlePrintPDF(), 500);
      return () => clearTimeout(timer);
    }
  }, [ready]); // eslint-disable-line

  if (!sku) {
    return <div className="flex items-center justify-center min-h-screen"><p>No SKU provided</p></div>;
  }

  return (
    <div data-testid="barcode-print-page" style={{ padding: '24px', maxWidth: '500px' }}>
      <button onClick={() => window.history.back()} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <h2 className="text-lg font-bold mb-4">Print Barcode Label</h2>

      {/* Preview */}
      <div style={{
        width: '3.5in', padding: '6px 8px', border: '1px dashed #ccc',
        fontFamily: 'Arial, Helvetica, sans-serif', display: 'flex', flexDirection: 'column',
        justifyContent: 'center', marginBottom: '16px', background: 'white',
      }} data-testid="barcode-label">
        <div style={{ fontSize: '9pt', fontWeight: 'bold', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: description ? '0' : '4px' }}>
          {name}
        </div>
        {description && (
          <div style={{ fontSize: '7pt', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#555', marginBottom: '4px' }}>
            {description}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg ref={svgRef} />
        </div>
        <div style={{ fontFamily: "'Courier New', monospace", fontSize: '8pt', textAlign: 'center', marginTop: '4px', letterSpacing: '0.03em' }}>
          {sku}
        </div>
      </div>

      {error && <p style={{ color: 'red', fontSize: '14px', marginBottom: '16px' }}>{error}</p>}
      <p className="text-xs text-muted-foreground mb-4">
        Dymo 30252 (3.5" x 1.125") — Opens as PDF for reliable printing
      </p>
      <button onClick={handlePrintPDF} className="px-8 py-3 bg-primary text-white rounded-md font-semibold hover:bg-primary/90" data-testid="print-now-btn">
        <Printer className="h-4 w-4 inline mr-2" /> Print Barcode Label
      </button>
    </div>
  );
}
